/**
 * Custom Select / Combobox (§18) — one dropdown used everywhere (filters,
 * country picker). Replaces the native <select> for consistent styling + an
 * elevated menu with hover/selected/active states and a check on the selected
 * option. Fully keyboard-operable:
 *   - closed trigger: Enter/Space/↓ opens
 *   - open: ↑/↓ move the active option, Enter selects, Esc/Tab close, click-out closes
 *   - non-searchable: type-ahead jumps to the option whose label starts with what
 *     you type; searchable: a filter input narrows the list (country picker).
 * ARIA: trigger is aria-haspopup=listbox/aria-expanded; menu is role=listbox with
 * role=option + aria-selected; the active option is tracked via aria-activedescendant.
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { moveSelection } from "../../lib/keyboardNav.js";
import { Icon } from "./Icon.js";

export interface SelectOption {
  value: string;
  label: string;
}

export function Select({
  value,
  onChange,
  options,
  placeholder = "Select…",
  ariaLabel,
  searchable = false,
  searchPlaceholder = "Type to search…",
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  ariaLabel?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const optionRefs = useRef<Map<number, HTMLLIElement>>(new Map());
  const typeahead = useRef<{ buf: string; at: number }>({ buf: "", at: 0 });
  const baseId = useId();

  const selected = options.find((o) => o.value === value) ?? null;
  const shown =
    searchable && query.trim()
      ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
      : options;

  const close = useCallback((focusTrigger = true) => {
    setOpen(false);
    setQuery("");
    setActive(-1);
    if (focusTrigger) triggerRef.current?.focus();
  }, []);

  const choose = useCallback(
    (v: string) => {
      onChange(v);
      close();
    },
    [onChange, close],
  );

  // Open with the current selection (or first) active.
  const openMenu = useCallback(() => {
    setOpen(true);
    const idx = selected ? options.findIndex((o) => o.value === selected.value) : 0;
    setActive(idx >= 0 ? idx : 0);
  }, [options, selected]);

  // Click-outside closes (no focus return — focus is already elsewhere).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, close]);

  // Focus the search box (searchable) or the list (not) when the menu opens.
  useEffect(() => {
    if (!open) return;
    if (searchable) searchRef.current?.focus();
    else listRef.current?.focus();
  }, [open, searchable]);
  useEffect(() => {
    if (open && active >= 0) optionRefs.current.get(active)?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => moveSelection(i, 1, shown.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => moveSelection(i, -1, shown.length));
    } else if (e.key === "Home") {
      e.preventDefault();
      setActive(shown.length ? 0 : -1);
    } else if (e.key === "End") {
      e.preventDefault();
      setActive(shown.length - 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = active >= 0 ? shown[active] : undefined;
      if (opt) choose(opt.value);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "Tab") {
      e.preventDefault();
      close(); // return focus to the trigger so tab order stays predictable
    } else if (!searchable && e.key.length === 1 && /\S/.test(e.key)) {
      // type-ahead over labels (non-searchable mode)
      const now = Date.now();
      const ta = typeahead.current;
      ta.buf = now - ta.at > 600 ? e.key : ta.buf + e.key;
      ta.at = now;
      const q = ta.buf.toLowerCase();
      const idx = shown.findIndex((o) => o.label.toLowerCase().startsWith(q));
      if (idx >= 0) setActive(idx);
    }
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      openMenu();
    }
  };

  const listboxId = `${baseId}-listbox`;
  // Only reference an option id that actually exists in the rendered list.
  const activeId = active >= 0 && active < shown.length ? `${baseId}-opt-${active}` : undefined;

  return (
    <div ref={rootRef} className={`xselect${className ? ` ${className}` : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className="xselect__trigger control"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={selected ? "xselect__value" : "xselect__value xselect__value--placeholder"}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="xselect__chevron" aria-hidden="true" />
      </button>

      {open && (
        <div className="xselect__menu" role="presentation" onKeyDown={onListKeyDown}>
          {searchable && (
            <div className="xselect__search">
              <Icon name="search" size={14} />
              <input
                ref={searchRef}
                className="xselect__search-input"
                placeholder={searchPlaceholder}
                aria-label={ariaLabel ?? "Search"}
                value={query}
                role="combobox"
                aria-expanded
                aria-haspopup="listbox"
                aria-autocomplete="list"
                aria-controls={listboxId}
                aria-activedescendant={activeId}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
              />
            </div>
          )}
          <ul
            ref={listRef}
            id={listboxId}
            className="xselect__list"
            role="listbox"
            aria-label={ariaLabel}
            tabIndex={searchable ? -1 : 0}
            aria-activedescendant={searchable ? undefined : activeId}
            onKeyDown={searchable ? undefined : onListKeyDown}
          >
            {shown.length === 0 ? (
              <li className="xselect__empty" role="presentation">
                No matches
              </li>
            ) : (
              shown.map((o, i) => {
                const isSel = o.value === value;
                return (
                  <li
                    key={o.value}
                    id={`${baseId}-opt-${i}`}
                    ref={(el) => {
                      if (el) optionRefs.current.set(i, el);
                      else optionRefs.current.delete(i);
                    }}
                    role="option"
                    aria-selected={isSel}
                    className={"xselect__option" + (i === active ? " xselect__option--active" : "") + (isSel ? " xselect__option--selected" : "")}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => choose(o.value)}
                  >
                    <span className="xselect__check" aria-hidden="true">
                      {isSel && <Icon name="check" size={14} />}
                    </span>
                    {o.label}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
